import React, { useContext, useEffect } from "react"
import Modal from "../../../../ui/Modal"
import { validDurations, validElectric } from "~/types"
import { PRICE_DURATIONS } from "~/constants/price_durations"
import { ELECTRICS } from "~/constants"
import { TagIcon } from "@heroicons/react/20/solid"
import InputFieldWithCurrency from "../../../Inputs/InputFieldWithCurrency"
import { AdminContext } from "~/contexts/AdminContext"
import Button from "../../../../ui/Button"
import classNames from "../../../../../utils/classNames"
import SelectInput, { Option } from "../../../../ui/SelectInput"
import { useForm, Controller } from "react-hook-form"

interface AddEditPriceModalProps {
  open: boolean
  onClose: () => void
  isEditMode?: boolean
  initialPriceData?: {
    amount_in_cents: number
    duration: validDurations
    electric: validElectric
  }
  onSubmit: (priceData: { amount_in_cents: number; duration: validDurations; electric: validElectric }) => void
  onRemove?: () => void
  isPriceUsed: boolean
}

interface FormValues {
  amountInDollars: number
  duration: validDurations
  electric: validElectric
}

const AddEditPriceModal = ({
  open,
  onClose,
  isEditMode = false,
  initialPriceData,
  onSubmit,
  onRemove,
  isPriceUsed,
}: AddEditPriceModalProps) => {
  const { camp } = useContext(AdminContext)

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      amountInDollars: initialPriceData?.amount_in_cents ? initialPriceData.amount_in_cents / 100 : 0,
      duration: initialPriceData?.duration || PRICE_DURATIONS.NIGHTLY,
      electric: initialPriceData?.electric || ELECTRICS.NONE,
    },
  })

  useEffect(() => {
    if (open) {
      if (isEditMode && initialPriceData) {
        reset({
          amountInDollars: initialPriceData.amount_in_cents / 100,
          duration: initialPriceData.duration,
          electric: initialPriceData.electric,
        })
      } else {
        reset({
          amountInDollars: 0,
          duration: PRICE_DURATIONS.NIGHTLY,
          electric: ELECTRICS.NONE,
        })
      }
    }
  }, [open, isEditMode, initialPriceData, reset])

  const internalHandleSubmit = (data: FormValues) => {
    const priceData = {
      amount_in_cents: Math.round(data.amountInDollars),
      duration: data.duration,
      electric: data.electric,
    }
    onSubmit(priceData)
    onClose()
  }

  const electricOptions = Object.entries(ELECTRICS).map(([key, value]) => ({
    label: key,
    value,
  })) as Array<Option>

  return (
    <Modal open={open} onClose={onClose} className="z-40" maxWidth="max-w-3xl">
      <form onSubmit={handleSubmit(internalHandleSubmit)} className="p-1 space-y-6">
        <h2 className="text-2xl font-semibold flex items-center">
          <div className="p-2 rounded-full bg w-10 h-10 bg-blue-200 mr-3">
            <TagIcon className="w-full h-full text-blue-400" />
          </div>
          {isEditMode ? "Edit Price" : "New Price"}
        </h2>
        <div className="px-12 pt-2 pb-5 space-y-4">
          <div>
            <Controller
              name="amountInDollars"
              control={control}
              render={({ field }) => (
                <InputFieldWithCurrency
                  value={field.value}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value)
                    field.onChange(isNaN(value) ? 0 : value)
                  }}
                  currency={camp?.currency}
                  label="Amount"
                  min={0}
                  className="w-72"
                />
              )}
            />
          </div>
          <div>
            <label className="block font-semibold mb-2">Duration</label>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(PRICE_DURATIONS).map(([key, value]) => (
                <Controller
                  key={key}
                  name="duration"
                  control={control}
                  render={({ field }) => (
                    <Button
                      type="button"
                      disabled={isEditMode}
                      onClick={() => field.onChange(value)}
                      variant="greenOutline"
                      className={classNames(
                        field.value === value && "border-2 font-semibold border-green-600 text-green-700",
                      )}
                    >
                      {value}
                    </Button>
                  )}
                />
              ))}
            </div>
          </div>
          <div className="w-72">
            <label className="block font-semibold mb-2">Electric</label>
            <Controller
              name="electric"
              control={control}
              render={({ field }) => (
                <SelectInput
                  disabled={isEditMode}
                  options={electricOptions}
                  selected={electricOptions.find((option) => option.value === field.value)}
                  setSelected={(option: Option) => field.onChange(option.value)}
                  large
                  noIcon
                />
              )}
            />
          </div>
        </div>
        <div className="flex justify-between items-center mt-6 w-full">
          {isEditMode && (
            <Button onClick={onRemove} variant="link" className="font-semibold text-lg text-red-400" disabled={true}>
              Remove Price
            </Button>
          )}
          <div className="flex space-x-4 ml-auto">
            <Button onClick={onClose} variant="gray" type="button">
              Cancel
            </Button>
            <Button variant="blue" type="submit" disabled={isEditMode || !isDirty}>
              {isEditMode ? "Update Price" : "Create Price"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default AddEditPriceModal
